import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { API, useApp } from "../context/AppContext";
import GeoArt from "./GeoArt";
import { MaskedLines } from "./Reveal";

const COPY = {
  soon: {
    headline: ["Something Bold", "Is Loading"],
    sub: "Art. Community. Technology. A new cultural platform from Atuarra McCaslin is almost here.",
  },
  studio: {
    headline: ["Back In", "The Studio"],
    sub: "We're making the work better. The doors reopen shortly — leave your email and we'll tell you the moment they do.",
  },
};

function GatePage({ mode }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const copy = COPY[mode] || COPY.soon;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email });
      setDone(true);
      toast.success("You're on the list.");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
  };

  return (
    <main className="relative min-h-screen flex items-center overflow-hidden grain pt-16" data-testid={`gate-${mode}`}>
      <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 opacity-25">
        {["gate-a", "gate-b", "gate-c", "gate-d"].map((s, i) => (
          <div key={s} className={i % 2 ? "mt-16" : ""}>
            <GeoArt seed={`${mode}-${s}`} className="w-full h-full" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
      <div className="relative container-x py-24 w-full">
        <div className="flex items-center gap-3 mb-10">
          <span className="w-10 h-10 bg-[#FF1A1A] flex items-center justify-center font-display font-black text-white">AM</span>
          <span className="font-display font-extrabold uppercase">Atuarra McCaslin</span>
        </div>
        <MaskedLines lines={copy.headline} className="h-display !text-5xl sm:!text-7xl lg:!text-8xl text-[#FF1A1A]" />
        <p className="mt-8 max-w-xl text-muted-foreground" data-testid="gate-subline">{copy.sub}</p>
        <div className="mt-10 max-w-md" data-testid="gate-notify">
          {done ? (
            <p className="font-display font-bold uppercase text-[#FF1A1A]" data-testid="gate-notify-success">You're in. We'll be loud when the doors open.</p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="gate-notify-form">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="input-x flex-1" data-testid="gate-email-input" aria-label="Email for launch notification" />
              <button type="submit" className="btn-red" data-testid="gate-notify-submit">Get Notified</button>
            </form>
          )}
        </div>
        <div className="mt-12 flex items-center gap-8 text-xs font-display font-bold uppercase tracking-widest">
          <a href="https://instagram.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#FF1A1A] transition-colors" data-testid="gate-instagram">
            Instagram <ArrowUpRight size={12} />
          </a>
          <Link to="/admin" className="text-muted-foreground hover:text-[#FF1A1A] transition-colors" data-testid="gate-admin-link">Studio Sign In</Link>
        </div>
      </div>
    </main>
  );
}

export default function SiteGate({ children }) {
  const { siteMode, admin, checkAdmin } = useApp();
  const { pathname } = useLocation();

  const gated = siteMode === "soon" || siteMode === "studio";
  const isAdminRoute = pathname.startsWith("/admin");

  useEffect(() => {
    if (gated && admin === null) checkAdmin();
  }, [gated, admin]);

  if (isAdminRoute || !gated || admin) return children;
  if (admin === null) return <main className="min-h-screen bg-background" data-testid="gate-loading" />;
  return <GatePage mode={siteMode} />;
}
